// frontend/src/pages/staff/StaffDashboard.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import OnboardingChecklist from "../../components/OnboardingChecklist";
import {
  getProfessionalCompletedSteps,
  shouldShowOnboarding,
} from "../../utils/onboardingHelpers";
import axios from "axios";
import WalkInButton from "../../components/WalkInButton";
import ShareProfileButton from "../../components/ShareProfileButton";
import "./staffStyles.css";

const resolveProfessionalTipEarnings = (booking) => {
  const bookingTip = Number(booking?.tipAmount) || 0;
  const splitProfessionalTip = Number(
    booking?.paymentSplit?.professionalTipEarnings,
  );

  if (Number.isFinite(splitProfessionalTip)) {
    if (splitProfessionalTip > 0 || bookingTip <= 0) {
      return Math.max(0, splitProfessionalTip);
    }
  }

  const splitTipAmount = Number(booking?.paymentSplit?.tipAmount);
  if (Number.isFinite(splitTipAmount)) {
    if (splitTipAmount > 0 || bookingTip <= 0) {
      return Math.max(0, splitTipAmount);
    }
  }

  return Math.max(0, bookingTip);
};

const getProfessionalBookingBreakdown = (booking, staffData) => {
  if (!booking) {
    return { total: 0, service: 0, tip: 0 };
  }

  if (booking.paymentSplit?.professionalEarnings !== undefined) {
    const tip = resolveProfessionalTipEarnings(booking);
    const total = Number(booking.paymentSplit.professionalEarnings) || 0;
    const service =
      booking.paymentSplit.professionalServiceEarnings !== undefined
        ? Number(booking.paymentSplit.professionalServiceEarnings) || 0
        : Math.max(0, total - tip);

    return { total, service, tip };
  }

  const grossService = Number(booking.servicePriceAtBooking) || 0;
  const rawTip = Number(booking.tipAmount) || 0;

  if (staffData?.commissionType === "percentage") {
    const service =
      grossService * ((100 - (staffData.commissionRate || 0)) / 100);
    return { total: service + rawTip, service, tip: rawTip };
  }

  if (staffData?.commissionType === "flat") {
    const service = Math.max(
      0,
      grossService - (staffData.flatCommissionAmount || 0),
    );
    return { total: service + rawTip, service, tip: rawTip };
  }

  return { total: grossService + rawTip, service: grossService, tip: rawTip };
};

function StaffDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMsg, setErrorMsg] = useState("");
  const [subscribedBanner, setSubscribedBanner] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [scheduleSetupRequired, setScheduleSetupRequired] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productSales, setProductSales] = useState([]);
  const [loadingProductSales, setLoadingProductSales] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [loadingEarnings, setLoadingEarnings] = useState(true);

  // Phase E1: Onboarding state
  const [onboardingData, setOnboardingData] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show subscription success banner when redirected from direct-subscribe flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "true") {
      setSubscribedBanner(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      const timer = setTimeout(() => setSubscribedBanner(false), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Helper: Format time to 12-hour AM/PM
  const formatTime12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Determine role type - always 'professional' for all service providers
  const roleType = "professional";

  // Get professional type label (barber, nail tech, etc.) - always show "Professional"
  const getProfessionalTypeLabel = useCallback(() => {
    return "Professional";
  }, []);

  // Get role label for display - always "Professional"
  const getRoleLabel = useCallback(() => {
    if (profileData?.staff?.isFreelancer) return "Freelancer";
    return "Professional";
  }, [profileData]);

  // Get base path for navigation
  const getBasePath = useCallback(() => {
    return `/dashboard/professional`;
  }, []);

  // Security: Get auth headers with validation
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  // Phase E1: Fetch onboarding status
  const fetchOnboardingStatus = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      const baseURL = getApiBaseUrl();
      if (!baseURL) return;

      const response = await axios.get(
        `${baseURL}/api/users/onboarding/status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOnboardingData(response.data);
    } catch (error) {}
  }, [getApiBaseUrl]);

  // Phase E1: Handle onboarding step click
  const handleStepClick = useCallback(
    async (step) => {
      navigate(step.action);
    },
    [navigate],
  );

  // Phase E1: Handle onboarding dismiss
  const handleDismissOnboarding = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      const baseURL = getApiBaseUrl();
      if (!baseURL) return;

      const allSteps = ["photo", "services", "schedule"];
      const allComplete = allSteps.every((s) => completedSteps.includes(s));

      // If all steps are done, mark permanently complete so it never shows again.
      // Otherwise just dismiss (re-shows after 7 days).
      const endpoint = allComplete
        ? "/api/users/onboarding/complete"
        : "/api/users/onboarding/dismiss";

      await axios.post(
        `${baseURL}${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowOnboarding(false);
    } catch (error) {}
  }, [getApiBaseUrl, completedSteps]);

  const fetchProfile = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/me`, { headers });
      setProfileData(res.data);
    } catch (err) {
      // Security: Handle unauthorized/forbidden responses
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      }
    } finally {
      setLoadingProfile(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  const fetchServices = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/services`, { headers });
      setServices(res.data.services || []);
    } catch (err) {
      // Security: Handle unauthorized responses
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      }
    } finally {
      setLoadingServices(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  const fetchSchedule = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/schedule`, { headers });

      // Check if setup is required
      if (res.data.setupRequired) {
        setSchedule(null);
        setScheduleSetupRequired(true);
      } else {
        setSchedule(res.data.schedule);
        setScheduleSetupRequired(false);
      }
    } catch (err) {
      // Security: Handle unauthorized responses
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      }
    } finally {
      setLoadingSchedule(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  // Fetch today's and upcoming bookings
  const fetchBookings = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Get start of current month (need all month's bookings for "THIS MONTH" card)
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      // Get end of current month
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      // Fetch entire month to ensure we get all needed bookings
      const startDate = startOfMonth.toISOString().split("T")[0];
      const endDate = endOfMonth.toISOString().split("T")[0];

      const res = await axios.get(
        `${baseURL}/api/bookings/staff?startDate=${startDate}&endDate=${endDate}`,
        { headers },
      );
      const fetchedBookings = res.data.bookings || [];

      // Debug: Log fetched bookings
      setBookings(fetchedBookings);
    } catch (err) {
      // Don't show error for bookings, just log it
    } finally {
      setLoadingBookings(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const fetchProducts = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/my-products`, {
        headers,
      });
      setProducts(res.data.products || []);
    } catch (err) {
    } finally {
      setLoadingProducts(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const fetchProductSales = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/product-sales`, {
        headers,
      });
      setProductSales(res.data.productSales || []);
    } catch (err) {
    } finally {
      setLoadingProductSales(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const fetchNotifications = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/notifications`, {
        headers,
      });
      setNotifications(res.data.notifications || []);
    } catch (err) {
    } finally {
      setLoadingNotifications(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const fetchEarnings = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/earnings`, { headers });
      setEarnings(res.data);
    } catch (err) {
    } finally {
      setLoadingEarnings(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  // Security: Sanitize navigation to prevent open redirects
  const handleCardClick = useCallback(
    (path) => {
      const basePath = getBasePath();
      const fullPath = `${basePath}${path}`;

      // Validate path starts with expected base
      if (fullPath.startsWith("/dashboard/")) {
        navigate(fullPath);
      } else {
      }
    },
    [getBasePath, navigate],
  );

  // Load data on mount
  useEffect(() => {
    fetchProfile();
    fetchServices();
    fetchSchedule();
    fetchBookings();
    fetchProducts();
    fetchProductSales();
    fetchNotifications();
    fetchEarnings();
    fetchOnboardingStatus();
  }, [
    fetchProfile,
    fetchServices,
    fetchSchedule,
    fetchBookings,
    fetchProducts,
    fetchProductSales,
    fetchNotifications,
    fetchEarnings,
    fetchOnboardingStatus,
  ]);

  // Phase E1: Detect completed steps when data changes
  useEffect(() => {
    if (!onboardingData || loadingProfile || loadingServices) return;

    const hasPhoto = !!profileData?.user?.profilePicture;
    const hasServices = services.length > 0;
    const hasSchedule = !!schedule;
    const hasBankAccount = !!profileData?.staff?.bankAccount;

    const detected = getProfessionalCompletedSteps(
      hasPhoto,
      hasServices ? [{ id: "1" }] : [],
      hasSchedule ? { monday: {} } : null,
      hasBankAccount ? { last4: "1234" } : null,
    );

    const mergedSteps = [
      ...new Set([...(onboardingData.completedSteps || []), ...detected]),
    ];
    setCompletedSteps(mergedSteps);

    const allSteps = ["photo", "services", "schedule", "payment"];
    setShowOnboarding(
      shouldShowOnboarding(onboardingData, mergedSteps, allSteps),
    );
  }, [
    onboardingData,
    profileData,
    services,
    schedule,
    loadingProfile,
    loadingServices,
  ]);

  const roleLabel = getRoleLabel();

  return (
    <StaffSidebarLayout roleType={roleType}>
      <h3>{roleLabel} Dashboard</h3>

      {/* Walk-in (Phase 1): the professional defaults to themselves, so no
          provider selector is needed here. Deliberately separate from the
          paused PROVIDER_SELF_BOOKING_ENABLED add-booking flow. */}
      <div style={{ margin: "12px 0 20px" }}>
        <WalkInButton />
      </div>

      {/* Phase E1: Onboarding Checklist */}
      {showOnboarding && onboardingData && (
        <div style={{ marginBottom: "24px" }}>
          <OnboardingChecklist
            userType="professional"
            completedSteps={completedSteps}
            onStepClick={handleStepClick}
            onDismiss={handleDismissOnboarding}
          />
        </div>
      )}

      {/* Subscription success banner */}
      {subscribedBanner && (
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #86efac",
            borderRadius: "10px",
            padding: "14px 18px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "20px" }}>🎉</span>
            <span
              style={{ fontSize: "14px", fontWeight: "600", color: "#15803d" }}
            >
              Subscription activated! Welcome aboard — your full access is now
              restored.
            </span>
          </div>
          <button
            onClick={() => setSubscribedBanner(false)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#6b7280",
              fontSize: "18px",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Error banner */}
      {errorMsg && (
        <div
          className="dashboard-card"
          style={{ background: "#fff3f3", borderLeft: "4px solid #c00" }}
        >
          <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
        </div>
      )}

      <div className="dashboard-grid">
        {/* Profile Widget */}
        <div
          className="dashboard-card profile-card"
          style={{
            cursor: "pointer",
            overflow: "hidden",
            position: "relative",
          }}
          onClick={() => handleCardClick("/profile")}
        >
          {/* Decorative Background Pattern */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "100px",
              background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
              opacity: 0.1,
              zIndex: 0,
            }}
          />

          <h4 style={{ position: "relative", zIndex: 1 }}>Profile</h4>
          {loadingProfile ? (
            <p>Loading profile...</p>
          ) : profileData ? (
            <>
              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginTop: "16px",
                }}
              >
                {/* Profile Picture with Gradient Ring */}
                <div style={{ position: "relative", marginBottom: "16px" }}>
                  <div
                    style={{
                      position: "absolute",
                      inset: "-4px",
                      background:
                        "linear-gradient(135deg, #2d6da3 0%, #4a90c6 50%, #f093fb 100%)",
                      borderRadius: "50%",
                      animation: "spin 3s linear infinite",
                    }}
                  />
                  {profileData?.user?.profilePicture ? (
                    <img
                      src={profileData.user.profilePicture}
                      alt="Profile"
                      style={{
                        width: 90,
                        height: 90,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "4px solid white",
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 90,
                        height: 90,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 36,
                        fontWeight: "bold",
                        color: "white",
                        border: "4px solid white",
                        position: "relative",
                        zIndex: 1,
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                      }}
                    >
                      {profileData.user.firstName?.[0]}
                      {profileData.user.lastName?.[0]}
                    </div>
                  )}
                </div>

                {/* Name and Role */}
                <div style={{ textAlign: "center", marginBottom: "16px" }}>
                  <p
                    style={{
                      fontSize: 18,
                      fontWeight: "bold",
                      margin: "0 0 6px 0",
                      color: "#001d4a",
                    }}
                  >
                    {profileData.user.firstName} {profileData.user.lastName}
                  </p>
                  <div
                    style={{
                      display: "inline-block",
                      padding: "4px 12px",
                      background:
                        "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                      borderRadius: "20px",
                      fontSize: 13,
                      fontWeight: "600",
                      color: "white",
                      boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                    }}
                  >
                    {roleLabel}
                  </div>
                </div>

                {/* Stats Row */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                    width: "100%",
                    marginBottom: "16px",
                  }}
                >
                  {/* Rating */}
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px 8px",
                      background: "#fef3c7",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ fontSize: 20, marginBottom: "4px" }}>⭐</div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: "bold",
                        color: "#b45309",
                      }}
                    >
                      {profileData.staff.rating > 0
                        ? profileData.staff.rating.toFixed(1)
                        : "New"}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#92400e",
                        marginTop: "2px",
                      }}
                    >
                      Rating
                    </div>
                  </div>

                  {/* Services */}
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px 8px",
                      background: "#dbeafe",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ fontSize: 20, marginBottom: "4px" }}>💼</div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: "bold",
                        color: "#1e40af",
                      }}
                    >
                      {services.filter((s) => s.isActive).length}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#1e3a8a",
                        marginTop: "2px",
                      }}
                    >
                      Services
                    </div>
                  </div>

                  {/* Status */}
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px 8px",
                      background: "#d1fae5",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ fontSize: 20, marginBottom: "4px" }}>✓</div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: "bold",
                        color: "#065f46",
                      }}
                    >
                      {profileData.staff.status}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#064e3b",
                        marginTop: "2px",
                      }}
                    >
                      Status
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                {profileData.user.email && (
                  <div
                    style={{
                      width: "100%",
                      padding: "12px",
                      background: "#f9fafb",
                      borderRadius: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: "4px",
                      }}
                    >
                      📧 Email
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#374151",
                        fontWeight: "500",
                      }}
                    >
                      {profileData.user.email}
                    </div>
                  </div>
                )}

                {profileData?.staff?.slug &&
                  (() => {
                    const origin = window.location.origin;
                    const staffSlug = profileData.staff.slug;
                    const bizSlug = profileData.staff.businessSlug;
                    const locationSlug =
                      profileData.staff.activeLocation?.slug ||
                      profileData.staff.activeLocationSlug;
                    const role = profileData.staff.role;
                    const shareUrl =
                      role === "R4" || role === "R7"
                        ? `${origin}/freelancer/${staffSlug}`
                        : bizSlug && locationSlug
                          ? `${origin}/business/${bizSlug}/${locationSlug}/professional/${staffSlug}`
                          : `${origin}/professional/${staffSlug}`;
                    const name =
                      `${profileData.user.firstName || ""} ${profileData.user.lastName || ""}`.trim();
                    return (
                      <div style={{ marginBottom: "12px" }}>
                        <ShareProfileButton
                          url={shareUrl}
                          title={
                            name
                              ? `${name} on Ogestyle`
                              : "My profile on Ogestyle"
                          }
                        />
                      </div>
                    );
                  })()}
                <button
                  className="btn-primary"
                  style={{
                    width: "100%",
                    background:
                      "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                    border: "none",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick("/profile");
                  }}
                >
                  Edit Profile
                </button>
              </div>
            </>
          ) : (
            <>
              <p>Unable to load profile</p>
              <button
                className="btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  fetchProfile();
                }}
              >
                Retry
              </button>
            </>
          )}
        </div>

        {/* Notifications & Earnings Row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          {/* Notifications Widget - Include Setup Prompts */}
          <div
            className="dashboard-card earnings-card"
            style={{ cursor: "pointer", margin: 0 }}
            onClick={() => handleCardClick("/earnings")}
          >
            <h4>Earnings</h4>
            {loadingBookings ? (
              <p>Loading earnings...</p>
            ) : (
              (() => {
                const isImportedBooking = (booking) =>
                  booking?.isImported === true ||
                  booking?.paidInExternalSystem === true ||
                  booking?.bookingType === "manual" ||
                  !!booking?.importedAt;
                const staffData = profileData?.staff || profileData;

                const completedBookings = bookings.filter(
                  (b) => b.status === "completed" && !isImportedBooking(b),
                );

                // Calculate earnings using same calendar periods as bookings overview
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Start of current week (Sunday)
                const startOfWeek = new Date(today);
                startOfWeek.setDate(today.getDate() - today.getDay());
                startOfWeek.setHours(0, 0, 0, 0);

                // End of current week (Saturday)
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                endOfWeek.setHours(23, 59, 59, 999);

                // Start of current month (1st)
                const startOfMonth = new Date(
                  today.getFullYear(),
                  today.getMonth(),
                  1,
                );
                startOfMonth.setHours(0, 0, 0, 0);

                // End of current month (last day)
                const endOfMonth = new Date(
                  today.getFullYear(),
                  today.getMonth() + 1,
                  0,
                  23,
                  59,
                  59,
                  999,
                );

                // Today's earnings - completed bookings today (commission-adjusted)
                const todayEarnings = completedBookings
                  .filter((b) => {
                    // Parse in local timezone to avoid UTC conversion issues
                    const [year, month, day] = b.appointmentDate
                      .split("-")
                      .map(Number);
                    const bookingDate = new Date(year, month - 1, day);
                    return bookingDate.getTime() === today.getTime();
                  })
                  .reduce((sum, b) => {
                    return (
                      sum + getProfessionalBookingBreakdown(b, staffData).total
                    );
                  }, 0);

                // This week's earnings - completed bookings in calendar week (commission-adjusted)
                const weekEarnings = completedBookings
                  .filter((b) => {
                    // Parse in local timezone to avoid UTC conversion issues
                    const [year, month, day] = b.appointmentDate
                      .split("-")
                      .map(Number);
                    const bookingDate = new Date(year, month - 1, day);
                    return (
                      bookingDate >= startOfWeek && bookingDate <= endOfWeek
                    );
                  })
                  .reduce((sum, b) => {
                    return (
                      sum + getProfessionalBookingBreakdown(b, staffData).total
                    );
                  }, 0);

                // This month's earnings - use the same booking basis as today/week.
                const monthEarnings = completedBookings
                  .filter((b) => {
                    const [year, month, day] = b.appointmentDate
                      .split("-")
                      .map(Number);
                    const bookingDate = new Date(year, month - 1, day);
                    return (
                      bookingDate >= startOfMonth && bookingDate <= endOfMonth
                    );
                  })
                  .reduce(
                    (sum, b) =>
                      sum + getProfessionalBookingBreakdown(b, staffData).total,
                    0,
                  );

                // Count of completed bookings for this month (for breakdown)
                const thisMonthCompleted = completedBookings.filter((b) => {
                  // Parse in local timezone to avoid UTC conversion issues
                  const [year, month, day] = b.appointmentDate
                    .split("-")
                    .map(Number);
                  const bookingDate = new Date(year, month - 1, day);
                  return (
                    bookingDate >= startOfMonth && bookingDate <= endOfMonth
                  );
                }).length;

                return (
                  <>
                    {/* Earnings Stats Grid */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "12px",
                        margin: "20px 0",
                      }}
                    >
                      {/* Today */}
                      <div
                        style={{
                          padding: "16px",
                          background:
                            "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                          borderRadius: "12px",
                          textAlign: "center",
                          color: "white",
                          boxShadow: "0 4px 6px rgba(16, 185, 129, 0.3)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            marginBottom: "8px",
                          }}
                        >
                          TODAY
                        </div>
                        <div
                          style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          ${todayEarnings.toFixed(2)}
                        </div>
                      </div>

                      {/* This Week */}
                      <div
                        style={{
                          padding: "16px",
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                          borderRadius: "12px",
                          textAlign: "center",
                          color: "white",
                          boxShadow: "0 4px 6px rgba(59, 130, 246, 0.3)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            marginBottom: "8px",
                          }}
                        >
                          THIS WEEK
                        </div>
                        <div
                          style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          ${weekEarnings.toFixed(2)}
                        </div>
                      </div>

                      {/* This Month */}
                      <div
                        style={{
                          padding: "16px",
                          background:
                            "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                          borderRadius: "12px",
                          textAlign: "center",
                          color: "white",
                          boxShadow: "0 4px 6px rgba(139, 92, 246, 0.3)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            marginBottom: "8px",
                          }}
                        >
                          THIS MONTH
                        </div>
                        <div
                          style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          ${monthEarnings.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    {/* Breakdown */}
                    <div
                      style={{
                        padding: "12px",
                        background: "#f9fafb",
                        borderRadius: "8px",
                        marginBottom: "12px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                          margin: "0 0 8px 0",
                          textTransform: "uppercase",
                        }}
                      >
                        This Month Breakdown
                      </p>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "8px",
                          fontSize: "13px",
                        }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            background: "white",
                            borderRadius: "6px",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "bold",
                              color: "#16a34a",
                              fontSize: "18px",
                            }}
                          >
                            {thisMonthCompleted}
                          </div>
                          <div style={{ color: "#6b7280", fontSize: "11px" }}>
                            Completed
                          </div>
                        </div>
                        <div
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            background: "white",
                            borderRadius: "6px",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "bold",
                              color: "#2563eb",
                              fontSize: "18px",
                            }}
                          >
                            $
                            {thisMonthCompleted > 0
                              ? (monthEarnings / thisMonthCompleted).toFixed(2)
                              : "0.00"}
                          </div>
                          <div style={{ color: "#6b7280", fontSize: "11px" }}>
                            Avg/Service
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      className="btn-primary"
                      style={{ width: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCardClick("/earnings");
                      }}
                    >
                      View Detailed Earnings
                    </button>
                  </>
                );
              })()
            )}
          </div>
        </div>

        {/* Bookings Stats Widget */}
        <div
          className="dashboard-card bookings-card"
          style={{ cursor: "pointer" }}
          onClick={() => handleCardClick("/bookings")}
        >
          <h4>Bookings Overview</h4>
          {loadingBookings ? (
            <p>Loading bookings...</p>
          ) : (
            <>
              {(() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Start of current week (Sunday)
                const startOfWeek = new Date(today);
                startOfWeek.setDate(today.getDate() - today.getDay());
                startOfWeek.setHours(0, 0, 0, 0);

                // End of current week (Saturday)
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                endOfWeek.setHours(23, 59, 59, 999);

                // Start of current month (1st)
                const startOfMonth = new Date(
                  today.getFullYear(),
                  today.getMonth(),
                  1,
                );
                startOfMonth.setHours(0, 0, 0, 0);

                // End of current month (last day)
                const endOfMonth = new Date(
                  today.getFullYear(),
                  today.getMonth() + 1,
                  0,
                  23,
                  59,
                  59,
                  999,
                );

                // Today: ALL bookings scheduled for today, regardless of status
                // (cancelled, no-show, completed etc. stay visible so the pro
                // always sees the full picture for the day).
                const todayBookings = bookings.filter((b) => {
                  if (!b.appointmentDate) return false;

                  // Parse in local timezone to avoid UTC conversion issues
                  const [year, month, day] = b.appointmentDate
                    .split("-")
                    .map(Number);
                  const bookingDate = new Date(year, month - 1, day);

                  return bookingDate.getTime() === today.getTime();
                });

                // This Week: all bookings in the current calendar week (Sunday-Saturday)
                const weekBookings = bookings.filter((b) => {
                  // Exclude cancelled and no-show bookings
                  if (b.status === "cancelled" || b.status === "no-show") {
                    return false;
                  }

                  // Parse in local timezone to avoid UTC conversion issues
                  const [year, month, day] = b.appointmentDate
                    .split("-")
                    .map(Number);
                  const bookingDate = new Date(year, month - 1, day);
                  const matches =
                    bookingDate >= startOfWeek && bookingDate <= endOfWeek;

                  // Debug log for week filtering
                  if (bookings.indexOf(b) < 5) {
                  }

                  return matches;
                });

                // This Month: all bookings in the current calendar month (1st to last day)
                const monthBookings = bookings.filter((b) => {
                  // Exclude cancelled and no-show bookings
                  if (b.status === "cancelled" || b.status === "no-show") {
                    return false;
                  }

                  // Parse in local timezone to avoid UTC conversion issues
                  const [year, month, day] = b.appointmentDate
                    .split("-")
                    .map(Number);
                  const bookingDate = new Date(year, month - 1, day);
                  return (
                    bookingDate >= startOfMonth && bookingDate <= endOfMonth
                  );
                });

                return (
                  <>
                    {/* Booking Stats Grid */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "12px",
                        margin: "20px 0",
                      }}
                    >
                      {/* Today */}
                      <div
                        style={{
                          padding: "16px",
                          background:
                            "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                          borderRadius: "12px",
                          textAlign: "center",
                          color: "white",
                          boxShadow: "0 4px 6px rgba(102, 126, 234, 0.3)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            marginBottom: "8px",
                          }}
                        >
                          TODAY
                        </div>
                        <div
                          style={{
                            fontSize: "36px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          {todayBookings.length}
                        </div>
                      </div>

                      {/* This Week */}
                      <div
                        style={{
                          padding: "16px",
                          background:
                            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                          borderRadius: "12px",
                          textAlign: "center",
                          color: "white",
                          boxShadow: "0 4px 6px rgba(240, 147, 251, 0.3)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            marginBottom: "8px",
                          }}
                        >
                          THIS WEEK
                        </div>
                        <div
                          style={{
                            fontSize: "36px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          {weekBookings.length}
                        </div>
                      </div>

                      {/* This Month */}
                      <div
                        style={{
                          padding: "16px",
                          background:
                            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                          borderRadius: "12px",
                          textAlign: "center",
                          color: "white",
                          boxShadow: "0 4px 6px rgba(79, 172, 254, 0.3)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            marginBottom: "8px",
                          }}
                        >
                          THIS MONTH
                        </div>
                        <div
                          style={{
                            fontSize: "36px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          {monthBookings.length}
                        </div>
                      </div>
                    </div>

                    {/* Today's Bookings Preview */}
                    {todayBookings.length > 0 ? (
                      <>
                        <div
                          style={{
                            padding: "12px",
                            background: "#f0fdf4",
                            borderLeft: "4px solid #16a34a",
                            borderRadius: "8px",
                            marginBottom: "16px",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#15803d",
                              margin: "0 0 8px 0",
                            }}
                          >
                            🎯 Next Upcoming Today:
                          </p>
                          {todayBookings.slice(0, 2).map((booking, idx) => (
                            <div
                              key={booking._id}
                              style={{
                                fontSize: "14px",
                                padding: "6px 0",
                                borderTop:
                                  idx > 0 ? "1px solid #bbf7d0" : "none",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginBottom: "2px",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: "500",
                                    color: "#166534",
                                  }}
                                >
                                  {formatTime12Hour(booking.startTime)}
                                </span>
                                <span
                                  style={{ fontSize: "12px", color: "#16a34a" }}
                                >
                                  {booking.customerName}
                                </span>
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#15803d" }}
                              >
                                {booking.serviceId?.name || "Service"}
                              </div>
                            </div>
                          ))}
                          {todayBookings.length > 2 && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#15803d",
                                marginTop: "8px",
                                fontStyle: "italic",
                              }}
                            >
                              +{todayBookings.length - 2} more today
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div
                        style={{
                          padding: "16px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          textAlign: "center",
                          marginBottom: "16px",
                        }}
                      >
                        <p
                          style={{ fontSize: "14px", color: "#666", margin: 0 }}
                        >
                          📅 No bookings scheduled for today
                        </p>
                      </div>
                    )}

                    {/* Monthly Totals Summary */}
                    <div
                      style={{
                        padding: "12px",
                        background: "#f9fafb",
                        borderRadius: "8px",
                        marginBottom: "12px",
                        borderTop: "2px solid #e5e7eb",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                          margin: "0 0 8px 0",
                          textTransform: "uppercase",
                        }}
                      >
                        This Month Totals
                      </p>
                      {(() => {
                        const startOfMonth = new Date(
                          today.getFullYear(),
                          today.getMonth(),
                          1,
                        );
                        const endOfMonth = new Date(
                          today.getFullYear(),
                          today.getMonth() + 1,
                          0,
                          23,
                          59,
                          59,
                          999,
                        );

                        const allMonthBookings = bookings.filter((b) => {
                          const bookingDate = new Date(b.appointmentDate);
                          return (
                            bookingDate >= startOfMonth &&
                            bookingDate <= endOfMonth
                          );
                        });

                        const completed = allMonthBookings.filter(
                          (b) => b.status === "completed",
                        ).length;
                        const cancelled = allMonthBookings.filter(
                          (b) => b.status === "cancelled",
                        ).length;
                        const noShow = allMonthBookings.filter(
                          (b) => b.status === "no-show",
                        ).length;
                        const total = allMonthBookings.length;

                        return (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(4, 1fr)",
                              gap: "8px",
                              fontSize: "13px",
                            }}
                          >
                            <div
                              style={{
                                textAlign: "center",
                                padding: "8px",
                                background: "white",
                                borderRadius: "6px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  color: "#001d4a",
                                  fontSize: "18px",
                                }}
                              >
                                {total}
                              </div>
                              <div
                                style={{ color: "#6b7280", fontSize: "11px" }}
                              >
                                Total
                              </div>
                            </div>
                            <div
                              style={{
                                textAlign: "center",
                                padding: "8px",
                                background: "white",
                                borderRadius: "6px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  color: "#16a34a",
                                  fontSize: "18px",
                                }}
                              >
                                {completed}
                              </div>
                              <div
                                style={{ color: "#6b7280", fontSize: "11px" }}
                              >
                                Completed
                              </div>
                            </div>
                            <div
                              style={{
                                textAlign: "center",
                                padding: "8px",
                                background: "white",
                                borderRadius: "6px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  color: "#dc2626",
                                  fontSize: "18px",
                                }}
                              >
                                {cancelled}
                              </div>
                              <div
                                style={{ color: "#6b7280", fontSize: "11px" }}
                              >
                                Cancelled
                              </div>
                            </div>
                            <div
                              style={{
                                textAlign: "center",
                                padding: "8px",
                                background: "white",
                                borderRadius: "6px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  color: "#9ca3af",
                                  fontSize: "18px",
                                }}
                              >
                                {noShow}
                              </div>
                              <div
                                style={{ color: "#6b7280", fontSize: "11px" }}
                              >
                                No-Show
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <button
                      className="btn-primary"
                      style={{ width: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCardClick("/bookings");
                      }}
                    >
                      View All Bookings
                    </button>
                  </>
                );
              })()}
            </>
          )}
        </div>

        {/* Earnings Widget */}
        <div
          className="dashboard-card notifications-card"
          style={{ cursor: "pointer" }}
          onClick={() => {
            // If there are setup items, don't navigate
            if (
              scheduleSetupRequired ||
              services.filter((s) => s.isActive).length === 0
            ) {
              return;
            }
            handleCardClick("/notifications");
          }}
        >
          <h4>
            📬{" "}
            {scheduleSetupRequired ||
            services.filter((s) => s.isActive).length === 0
              ? "Action Required"
              : "Notifications"}
          </h4>
          {/* Only show setup prompts after data has loaded to prevent flicker */}
          {loadingSchedule || loadingServices ? (
            <p style={{ fontSize: "13px", color: "#6b7280" }}>Loading...</p>
          ) : scheduleSetupRequired ||
            services.filter((s) => s.isActive).length === 0 ? (
            <>
              <p
                style={{
                  fontSize: "13px",
                  color: "#dc2626",
                  fontWeight: "600",
                  marginBottom: "16px",
                }}
              >
                Complete your profile to start accepting bookings
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {scheduleSetupRequired && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick("/schedule");
                    }}
                    style={{
                      padding: "12px",
                      background: "#fef3c7",
                      border: "2px solid #f59e0b",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.background = "#fde68a")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.background = "#fef3c7")
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ fontSize: "20px" }}>📅</span>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#92400e",
                          }}
                        >
                          Set Your Schedule
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#78350f",
                            marginTop: "2px",
                          }}
                        >
                          Define your working hours
                        </div>
                      </div>
                      <span style={{ color: "#f59e0b", fontSize: "16px" }}>
                        →
                      </span>
                    </div>
                  </div>
                )}
                {services.filter((s) => s.isActive).length === 0 && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick("/services");
                    }}
                    style={{
                      padding: "12px",
                      background: "#dbeafe",
                      border: "2px solid #3b82f6",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.background = "#bfdbfe")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.background = "#dbeafe")
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ fontSize: "20px" }}>💼</span>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1e40af",
                          }}
                        >
                          Add Services
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#1e3a8a",
                            marginTop: "2px",
                          }}
                        >
                          Create or select from templates
                        </div>
                      </div>
                      <span style={{ color: "#3b82f6", fontSize: "16px" }}>
                        →
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* View All Button for Setup Tasks */}
              <div
                style={{
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // For setup tasks, just keep them here - no separate page needed
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "transparent",
                    border: "none",
                    color: "#6366f1",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "default",
                    textAlign: "center",
                  }}
                >
                  Complete tasks above to activate profile
                </button>
              </div>
            </>
          ) : loadingNotifications ? (
            <p style={{ fontSize: "14px", color: "#9ca3af" }}>Loading...</p>
          ) : notifications.length > 0 ? (
            <>
              <ul style={{ listStyle: "none", padding: 0, margin: "12px 0" }}>
                {notifications.slice(0, 2).map((notif, idx) => (
                  <li
                    key={notif._id || idx}
                    style={{
                      padding: "8px 0",
                      borderBottom:
                        idx < notifications.slice(0, 2).length - 1
                          ? "1px solid #f3f4f6"
                          : "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "16px" }}>
                      {notif.type === "booking"
                        ? "📅"
                        : notif.type === "cancellation"
                          ? "❌"
                          : notif.type === "message"
                            ? "💬"
                            : "⭐"}
                    </span>
                    <span
                      style={{
                        fontSize: "13px",
                        flex: 1,
                        color: notif.read ? "#9ca3af" : "#374151",
                      }}
                    >
                      {notif.message.length > 35
                        ? notif.message.substring(0, 35) + "..."
                        : notif.message}
                    </span>
                    {!notif.read && (
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          background: "#ef4444",
                          borderRadius: "50%",
                        }}
                      />
                    )}
                  </li>
                ))}
              </ul>
              {notifications.filter((n) => !n.read).length > 0 && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    margin: "8px 0 12px",
                  }}
                >
                  {notifications.filter((n) => !n.read).length} unread
                </p>
              )}
            </>
          ) : (
            <p style={{ color: "#9ca3af", fontSize: "13px", margin: "12px 0" }}>
              No new notifications
            </p>
          )}
          <button
            className="btn-primary"
            style={{ fontSize: "14px", padding: "8px 16px" }}
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick("/notifications");
            }}
          >
            View All
          </button>
        </div>

        {/* Services Widget */}
        <div
          className="dashboard-card services-card"
          style={{ cursor: "pointer" }}
          onClick={() => handleCardClick("/services")}
        >
          <h4>Services</h4>
          {loadingServices ? (
            <p>Loading services...</p>
          ) : services.length > 0 ? (
            <>
              {/* Service Count Stats */}
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  margin: "20px 0",
                  padding: "16px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                }}
              >
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "33px",
                      fontWeight: "bold",
                      color: "#001d4a",
                    }}
                  >
                    {services.length}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Total Services
                  </div>
                </div>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "33px",
                      fontWeight: "bold",
                      color: "#15803d",
                    }}
                  >
                    {services.filter((s) => s.isActive).length}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Active
                  </div>
                </div>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "33px",
                      fontWeight: "bold",
                      color: "#c2410c",
                    }}
                  >
                    {services.filter((s) => !s.isActive).length}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Inactive
                  </div>
                </div>
              </div>

              {/* Recent Services Preview */}
              {services.filter((s) => s.isActive).length > 0 && (
                <>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#666",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Recent Active Services:
                  </p>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: "0 0 12px 0",
                    }}
                  >
                    {services
                      .filter((s) => s.isActive)
                      .slice(0, 2)
                      .map((service) => (
                        <li
                          key={service.id}
                          style={{
                            padding: "8px 0",
                            borderBottom: "1px solid #eee",
                            fontSize: "14px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{service.name}</span>
                            <span
                              style={{ color: "#03543f", fontWeight: "600" }}
                            >
                              ${service.price}
                            </span>
                          </div>
                        </li>
                      ))}
                  </ul>
                </>
              )}

              <button
                className="btn-primary"
                style={{ marginTop: "8px", width: "100%" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick("/services");
                }}
              >
                Manage Services
              </button>
            </>
          ) : (
            <>
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: "40px", marginBottom: "12px" }}>📋</div>
                <p
                  style={{
                    color: "#666",
                    margin: "0 0 16px 0",
                    fontSize: "14px",
                  }}
                >
                  No services added yet
                </p>
              </div>
              <button
                className="btn-primary"
                style={{ width: "100%" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick("/services");
                }}
              >
                Add Your First Service
              </button>
            </>
          )}
        </div>

        {/* Schedule Widget */}
        <div
          className="dashboard-card schedule-card"
          style={{ cursor: "pointer" }}
          onClick={() => handleCardClick("/schedule")}
        >
          <h4>Schedule</h4>
          {loadingSchedule ? (
            <p>Loading schedule...</p>
          ) : schedule ? (
            <>
              {(() => {
                const availableDays = Object.entries(schedule.weeklyHours || {})
                  .filter(([_, day]) => day.isAvailable)
                  .map(([dayKey, day]) => ({
                    name: dayKey.charAt(0).toUpperCase() + dayKey.slice(1, 3),
                    start: day.start,
                    end: day.end,
                  }));

                return availableDays.length > 0 ? (
                  <>
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#666",
                        fontWeight: "500",
                        marginBottom: "8px",
                      }}
                    >
                      Your Working Hours:
                    </p>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        margin: "0 0 12px 0",
                        fontSize: "15px",
                      }}
                    >
                      {availableDays.map((day, idx) => (
                        <li
                          key={idx}
                          style={{
                            padding: "6px 0",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <strong>{day.name}:</strong>{" "}
                          {formatTime12Hour(day.start)} -{" "}
                          {formatTime12Hour(day.end)}
                        </li>
                      ))}
                    </ul>
                    {schedule.bufferMinutes > 0 && (
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          marginBottom: "12px",
                        }}
                      >
                        Buffer: {schedule.bufferMinutes} min between
                        appointments
                      </p>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "16px 0" }}>
                    <div style={{ fontSize: "36px", marginBottom: "8px" }}>
                      📅
                    </div>
                    <p
                      style={{
                        color: "#666",
                        margin: "0 0 12px 0",
                        fontSize: "14px",
                      }}
                    >
                      No schedule set yet
                    </p>
                  </div>
                );
              })()}
              <button
                className="btn-primary"
                style={{ width: "100%" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick("/schedule");
                }}
              >
                {schedule.weeklyHours &&
                Object.values(schedule.weeklyHours).some((d) => d.isAvailable)
                  ? "Edit Schedule"
                  : "Set Schedule"}
              </button>
            </>
          ) : (
            <>
              <p style={{ color: "#666", margin: "16px 0" }}>
                Unable to load schedule
              </p>
              <button
                className="btn-primary"
                style={{ width: "100%" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick("/schedule");
                }}
              >
                Set Schedule
              </button>
            </>
          )}
        </div>

        {/* Products Widget - Only show if business has product sales enabled */}
        {profileData?.business?.enableProductSales && (
          <div
            className="dashboard-card products-card"
            style={{ cursor: "pointer" }}
            onClick={() => handleCardClick("/products")}
          >
            <h4
              style={{
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span style={{ fontSize: "24px" }}>📦</span>
              My Products
            </h4>
            {loadingProducts ? (
              <p
                style={{
                  color: "#666",
                  textAlign: "center",
                  padding: "20px 0",
                }}
              >
                Loading...
              </p>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      padding: "12px",
                      background: "#d1fae5",
                      borderRadius: "8px",
                      textAlign: "center",
                      border: "2px solid #10b981",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#059669",
                      }}
                    >
                      {
                        products.filter((p) => p.approvalStatus === "approved")
                          .length
                      }
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#065f46",
                        fontWeight: "600",
                      }}
                    >
                      APPROVED
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "12px",
                      background: "#fef3c7",
                      borderRadius: "8px",
                      textAlign: "center",
                      border: "2px solid #f59e0b",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#d97706",
                      }}
                    >
                      {
                        products.filter((p) => p.approvalStatus === "pending")
                          .length
                      }
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#92400e",
                        fontWeight: "600",
                      }}
                    >
                      PENDING
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "12px",
                      background: "#fee2e2",
                      borderRadius: "8px",
                      textAlign: "center",
                      border: "2px solid #ef4444",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#dc2626",
                      }}
                    >
                      {
                        products.filter((p) => p.approvalStatus === "rejected")
                          .length
                      }
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#991b1b",
                        fontWeight: "600",
                      }}
                    >
                      REJECTED
                    </div>
                  </div>
                </div>

                {/* Product Sales Stats */}
                {!loadingProductSales && productSales.length > 0 && (
                  <div
                    style={{
                      padding: "12px",
                      background:
                        "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                      borderRadius: "8px",
                      marginBottom: "12px",
                      color: "white",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        opacity: 0.9,
                        marginBottom: "8px",
                        textAlign: "center",
                      }}
                    >
                      💰 SALES THIS MONTH
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                          {(() => {
                            const thisMonth = new Date();
                            thisMonth.setDate(1);
                            thisMonth.setHours(0, 0, 0, 0);
                            return productSales.filter(
                              (order) => new Date(order.createdAt) >= thisMonth,
                            ).length;
                          })()}
                        </div>
                        <div style={{ fontSize: "11px", opacity: 0.9 }}>
                          Orders
                        </div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                          $
                          {(() => {
                            const thisMonth = new Date();
                            thisMonth.setDate(1);
                            thisMonth.setHours(0, 0, 0, 0);
                            const monthOrders = productSales.filter(
                              (order) => new Date(order.createdAt) >= thisMonth,
                            );
                            let total = 0;
                            monthOrders.forEach((order) => {
                              order.items.forEach((item) => {
                                if (
                                  item.ownedByStaffId &&
                                  item.professionalRevenue
                                ) {
                                  total += item.professionalRevenue;
                                }
                              });
                            });
                            return total.toFixed(2);
                          })()}
                        </div>
                        <div style={{ fontSize: "11px", opacity: 0.9 }}>
                          Earned
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {products.length > 0 ? (
                  <>
                    <div
                      style={{
                        padding: "12px",
                        background: "#f9fafb",
                        borderRadius: "8px",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ fontSize: "13px", color: "#6b7280" }}>
                          Revenue Per Sale:
                        </span>
                        <span
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#10b981",
                          }}
                        >
                          $
                          {products
                            .filter((p) => p.approvalStatus === "approved")
                            .reduce((sum, p) => {
                              const platformFee = (p.price * 3) / 100;
                              const shopCommission =
                                (p.price * (p.shopCommissionPercent || 12)) /
                                100;
                              return (
                                sum + (p.price - platformFee - shopCommission)
                              );
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#9ca3af",
                          marginTop: "4px",
                        }}
                      >
                        Across{" "}
                        {
                          products.filter(
                            (p) => p.approvalStatus === "approved",
                          ).length
                        }{" "}
                        approved products
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px 0",
                      color: "#6b7280",
                    }}
                  >
                    <p style={{ margin: "0 0 8px", fontSize: "14px" }}>
                      No products yet
                    </p>
                    <p style={{ margin: 0, fontSize: "12px" }}>
                      Start earning passive income!
                    </p>
                  </div>
                )}
              </>
            )}
            <button
              className="btn-primary"
              style={{ width: "100%" }}
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick("/products");
              }}
            >
              {products.length > 0 ? "Manage Products" : "Add Product"}
            </button>
          </div>
        )}
      </div>
    </StaffSidebarLayout>
  );
}

export default StaffDashboard;
